import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';

const NotificationContext = createContext();

// ─── Constants ────────────────────────────────────────────────────────────────
const DISMISS_DURATION = 3200; // ms before auto-dismiss
const EXIT_DURATION    = 260;  // ms for exit animation

// ─── Per-action visual config ─────────────────────────────────────────────────
const ACTION_CONFIG = {
  cart: {
    icon:        '✓',
    accentColor: '#16a34a',
    iconBg:      'linear-gradient(145deg, #d1fae5, #6ee7b7)',
    iconColor:   '#065f46',
    iconSize:    '16px',
    iconWeight:  '800',
    ctaBg:       'rgba(22, 163, 74, 0.10)',
    ctaColor:    '#15803d',
    ctaHoverBg:  'rgba(22, 163, 74, 0.18)',
    shadowColor: 'rgba(22, 163, 74, 0.18)',
  },
  wishlist: {
    icon:        '♥',
    accentColor: '#e11d48',
    iconBg:      'linear-gradient(145deg, #ffe4e6, #fda4af)',
    iconColor:   '#9f1239',
    iconSize:    '15px',
    iconWeight:  '700',
    ctaBg:       'rgba(225, 29, 72, 0.10)',
    ctaColor:    '#be123c',
    ctaHoverBg:  'rgba(225, 29, 72, 0.18)',
    shadowColor: 'rgba(225, 29, 72, 0.18)',
  },
  remove: {
    icon:        '×',
    accentColor: '#64748b',
    iconBg:      'linear-gradient(145deg, #f1f5f9, #cbd5e1)',
    iconColor:   '#334155',
    iconSize:    '22px',
    iconWeight:  '300',
    ctaBg:       'rgba(100, 116, 139, 0.10)',
    ctaColor:    '#475569',
    ctaHoverBg:  'rgba(100, 116, 139, 0.18)',
    shadowColor: 'rgba(100, 116, 139, 0.18)',
  },
  error: {
    icon:        '!',
    accentColor: '#dc2626',
    iconBg:      'linear-gradient(145deg, #fee2e2, #fca5a5)',
    iconColor:   '#7f1d1d',
    iconSize:    '15px',
    iconWeight:  '800',
    ctaBg:       'rgba(220, 38, 38, 0.10)',
    ctaColor:    '#b91c1c',
    ctaHoverBg:  'rgba(220, 38, 38, 0.18)',
    shadowColor: 'rgba(220, 38, 38, 0.18)',
  },
  info: {
    icon:        'i',
    accentColor: '#2563eb',
    iconBg:      'linear-gradient(145deg, #dbeafe, #93c5fd)',
    iconColor:   '#1e3a8a',
    iconSize:    '13px',
    iconWeight:  '800',
    fontFamily:  'Georgia, serif',
    ctaBg:       'rgba(37, 99, 235, 0.10)',
    ctaColor:    '#1d4ed8',
    ctaHoverBg:  'rgba(37, 99, 235, 0.18)',
    shadowColor: 'rgba(37, 99, 235, 0.18)',
  },
  success: {
    icon:        '✓',
    accentColor: '#16a34a',
    iconBg:      'linear-gradient(145deg, #d1fae5, #6ee7b7)',
    iconColor:   '#065f46',
    iconSize:    '16px',
    iconWeight:  '800',
    ctaBg:       'rgba(22, 163, 74, 0.10)',
    ctaColor:    '#15803d',
    ctaHoverBg:  'rgba(22, 163, 74, 0.18)',
    shadowColor: 'rgba(22, 163, 74, 0.18)',
  },
};

// ─── Scoped CSS injected once into <head> ─────────────────────────────────────
const TOAST_CSS = `
  /* ── Entry/exit — spring physics via cubic-bezier overshoot ── */
  @keyframes _ct_in_r {
    0%   { transform: translateX(calc(100% + 32px)) scale(0.88); opacity: 0; }
    100% { transform: translateX(0) scale(1); opacity: 1; }
  }
  @keyframes _ct_out_r {
    0%   { transform: translateX(0) scale(1); opacity: 1; }
    100% { transform: translateX(calc(100% + 32px)) scale(0.88); opacity: 0; }
  }
  @keyframes _ct_in_up {
    0%   { transform: translateX(-50%) translateY(110%) scale(0.90); opacity: 0; }
    100% { transform: translateX(-50%) translateY(0) scale(1); opacity: 1; }
  }
  @keyframes _ct_out_down {
    0%   { transform: translateX(-50%) translateY(0) scale(1); opacity: 1; }
    100% { transform: translateX(-50%) translateY(110%) scale(0.90); opacity: 0; }
  }
  @keyframes _ct_progress {
    from { transform: scaleX(1); }
    to   { transform: scaleX(0); }
  }

  /* ── Outer wrapper ── */
  ._ct_wrap {
    position: fixed;
    right: 24px;
    top: 28px;
    z-index: 99999;
    animation: _ct_in_r 0.46s cubic-bezier(0.34, 1.52, 0.64, 1) both;
    filter: drop-shadow(0 10px 32px var(--_ct_shadow, rgba(0,0,0,0.14)));
    transition: filter 0.3s ease;
    will-change: transform, opacity;
  }
  ._ct_wrap._ct_exit {
    animation: _ct_out_r ${EXIT_DURATION}ms cubic-bezier(0.4, 0, 0.8, 0.6) both !important;
    filter: drop-shadow(0 2px 6px rgba(0,0,0,0.06)) !important;
  }

  /* ── Card shell ── */
  ._ct_card {
    background: rgba(255, 255, 255, 0.97);
    backdrop-filter: blur(24px) saturate(1.6);
    -webkit-backdrop-filter: blur(24px) saturate(1.6);
    border-radius: 18px;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.055);
    min-width: 308px;
    max-width: 376px;
    overflow: hidden;
    position: relative;
  }

  /* Colored shimmer on top edge using accent CSS var */
  ._ct_card::after {
    content: '';
    position: absolute;
    top: 0; left: 18%; right: 18%;
    height: 1.5px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      var(--_ct_accent, #2563eb) 50%,
      transparent 100%
    );
    opacity: 0.55;
    pointer-events: none;
    z-index: 2;
  }

  /* ── Content row ── */
  ._ct_inner {
    display: flex;
    align-items: flex-start;
    gap: 13px;
    padding: 16px 13px 20px 18px;
  }

  /* ── Icon badge ── */
  ._ct_icon_wrap {
    width: 42px;
    height: 42px;
    border-radius: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow:
      0 2px 8px rgba(0, 0, 0, 0.12),
      0 0 0 1px rgba(0, 0, 0, 0.05);
    margin-top: 1px;
  }

  /* ── Product thumbnail ── */
  ._ct_thumb {
    width: 48px;
    height: 48px;
    border-radius: 13px;
    object-fit: cover;
    flex-shrink: 0;
    border: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.10);
    margin-top: 1px;
  }

  /* ── Text column ── */
  ._ct_text {
    flex: 1;
    min-width: 0;
    margin-top: 3px;
  }

  ._ct_title {
    font-size: 14px;
    font-weight: 600;
    color: #0f172a;
    line-height: 1.4;
    letter-spacing: -0.015em;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }
  ._ct_sub {
    font-size: 12.5px;
    color: #64748b;
    margin-top: 3px;
    line-height: 1.5;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }

  /* ── CTA pill button — styled via CSS vars per toast type ── */
  ._ct_cta_btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: 10px;
    font-size: 11.5px;
    font-weight: 700;
    background: var(--_ct_cta_bg, rgba(0, 0, 0, 0.07));
    border: none;
    padding: 5px 14px;
    cursor: pointer;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    border-radius: 99px;
    line-height: 1.4;
    transition: background 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
  }
  ._ct_cta_btn:hover {
    background: var(--_ct_cta_hover_bg, rgba(0, 0, 0, 0.12));
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }
  ._ct_cta_btn:active {
    transform: translateY(0);
    box-shadow: none;
  }

  /* ── Close button ── */
  ._ct_close_btn {
    width: 26px;
    height: 26px;
    background: rgba(0, 0, 0, 0.045);
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
    font-size: 12px;
    border-radius: 8px;
    flex-shrink: 0;
    line-height: 1;
    margin-top: 2px;
    transition: all 0.15s ease;
  }
  ._ct_close_btn:hover {
    color: #475569;
    background: rgba(0, 0, 0, 0.09);
    transform: scale(1.1);
  }
  ._ct_close_btn:active {
    transform: scale(0.94);
  }

  /* ── Left accent bar ── */
  ._ct_accent_bar {
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 4px;
    border-radius: 18px 0 0 18px;
    z-index: 1;
  }

  /* ── Progress rail + bar ── */
  ._ct_prog_rail {
    position: absolute;
    bottom: 0; left: 0; right: 0;
    height: 3px;
    overflow: hidden;
    border-radius: 0 0 18px 18px;
    background: rgba(0, 0, 0, 0.04);
  }
  ._ct_prog_bar {
    height: 100%;
    width: 100%;
    transform-origin: left center;
    border-radius: 0 0 18px 0;
    opacity: 0.80;
  }

  /* ── Mobile: bottom-centre with spring ── */
  @media (max-width: 640px) {
    ._ct_wrap {
      right: auto;
      top: auto;
      bottom: 22px;
      left: 50%;
      transform: translateX(-50%);
      animation: _ct_in_up 0.46s cubic-bezier(0.34, 1.52, 0.64, 1) both !important;
    }
    ._ct_wrap._ct_exit {
      animation: _ct_out_down ${EXIT_DURATION}ms cubic-bezier(0.4, 0, 0.8, 0.6) both !important;
    }
    ._ct_card {
      min-width: min(360px, calc(100vw - 32px));
      max-width: calc(100vw - 32px);
    }
  }
`;

// ─── Toast content (separate memo'd component to minimise re-renders) ─────────
const ToastContent = React.memo(({ notification, isHovered, dismiss }) => {
  const { message, type, options = {} } = notification;
  const { subtext, cta, ctaAction, thumbnail } = options;
  const config = ACTION_CONFIG[type] || ACTION_CONFIG.info;

  return (
    <>
      {/* Left accent bar */}
      <div className="_ct_accent_bar" style={{ background: config.accentColor }} />

      {/* Main content row */}
      <div className="_ct_inner">
        {thumbnail ? (
          <img
            src={thumbnail}
            alt=""
            className="_ct_thumb"
            onError={e => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div className="_ct_icon_wrap" style={{ background: config.iconBg }}>
            <span style={{
              fontSize:   config.iconSize,
              fontWeight: config.iconWeight,
              color:      config.iconColor,
              fontFamily: config.fontFamily || 'inherit',
              lineHeight: 1,
              display:    'block',
            }}>
              {config.icon}
            </span>
          </div>
        )}

        <div className="_ct_text">
          <div className="_ct_title">{message}</div>
          {subtext && <div className="_ct_sub">{subtext}</div>}
          {cta && (
            <button
              className="_ct_cta_btn"
              style={{
                '--_ct_cta_bg':       config.ctaBg,
                '--_ct_cta_hover_bg': config.ctaHoverBg,
                color:                config.ctaColor,
              }}
              onClick={() => { ctaAction?.(); dismiss(); }}
            >
              {cta}
            </button>
          )}
        </div>

        <button
          className="_ct_close_btn"
          onClick={dismiss}
          aria-label="Dismiss notification"
        >
          ✕
        </button>
      </div>

      {/* Auto-dismiss progress bar */}
      <div className="_ct_prog_rail">
        <div
          className="_ct_prog_bar"
          style={{
            background:         config.accentColor,
            animation:          `_ct_progress ${DISMISS_DURATION}ms linear both`,
            animationPlayState: isHovered ? 'paused' : 'running',
          }}
        />
      </div>
    </>
  );
});

// ─── Provider ─────────────────────────────────────────────────────────────────
export const NotificationProvider = ({ children }) => {
  const [notification, setNotification] = useState(null);
  const [isExiting,    setIsExiting]    = useState(false);
  const [isHovered,    setIsHovered]    = useState(false);

  const timerRef     = useRef(null);
  const exitTimerRef = useRef(null);
  const startTimeRef = useRef(null);
  const remainingRef = useRef(DISMISS_DURATION);

  // Inject scoped CSS once into <head>
  useEffect(() => {
    if (document.querySelector('[data-celer-toast]')) return;
    const el = document.createElement('style');
    el.setAttribute('data-celer-toast', '');
    el.textContent = TOAST_CSS;
    document.head.appendChild(el);
  }, []);

  // ── dismiss: play exit animation, then remove notification ──────────────────
  const dismiss = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    setIsExiting(true);
    exitTimerRef.current = setTimeout(() => {
      setNotification(null);
      setIsExiting(false);
      exitTimerRef.current = null;
    }, EXIT_DURATION);
  }, []);

  // ── notify(message, type?, options?) — public API ───────────────────────────
  const notify = useCallback((message, type = 'success', options = {}) => {
    if (timerRef.current)     { clearTimeout(timerRef.current);     timerRef.current = null; }
    if (exitTimerRef.current) { clearTimeout(exitTimerRef.current); exitTimerRef.current = null; }
    setIsExiting(false);
    setIsHovered(false);
    remainingRef.current = DISMISS_DURATION;
    setNotification({ message, type: type || 'success', options, id: Date.now() });
  }, []);

  // ── Auto-dismiss timer ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!notification) return;
    startTimeRef.current = Date.now();
    remainingRef.current = DISMISS_DURATION;
    timerRef.current     = setTimeout(dismiss, DISMISS_DURATION);
    return () => { if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; } };
  }, [notification?.id, dismiss]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Hover: pause auto-dismiss so user can read / click CTA ─────────────────
  const handleMouseEnter = useCallback(() => {
    if (!timerRef.current) return;
    const elapsed = Date.now() - (startTimeRef.current ?? Date.now());
    remainingRef.current = Math.max(500, (remainingRef.current ?? DISMISS_DURATION) - elapsed);
    clearTimeout(timerRef.current);
    timerRef.current = null;
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    if (!notification) return;
    startTimeRef.current = Date.now();
    timerRef.current = setTimeout(dismiss, remainingRef.current);
  }, [dismiss, notification]);

  // Derive per-notification accent vars (avoids re-render in children)
  const notifConfig = notification
    ? (ACTION_CONFIG[notification.type] || ACTION_CONFIG.info)
    : null;

  const ctxValue = useMemo(() => ({ notify }), [notify]);

  return (
    <NotificationContext.Provider value={ctxValue}>
      {children}

      {notification && (
        <div
          key={notification.id}
          className={`_ct_wrap${isExiting ? ' _ct_exit' : ''}`}
          style={{ '--_ct_shadow': notifConfig.shadowColor }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          role="alert"
          aria-live="polite"
          aria-atomic="true"
        >
          <div
            className="_ct_card"
            style={{ '--_ct_accent': notifConfig.accentColor }}
          >
            <ToastContent
              notification={notification}
              isHovered={isHovered}
              dismiss={dismiss}
            />
          </div>
        </div>
      )}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => useContext(NotificationContext);
